import { normalizeLocale, type SitePaymentCopyKey } from '@evi/shared';
import type { ElectricityEnrollment } from './bankWindowCopy';

/**
 * One locale for a payout page's copy, money and dates. The admin auth store has no stored user locale
 * (PublicUser carries none and /auth/me sends none), so this is the browser language when the app supports
 * it and English otherwise, so the numbers never follow a language the copy does not speak.
 */
export function payoutLocale(browser: string | null | undefined = typeof navigator === 'undefined' ? undefined : navigator.language): string {
  let tag = '';
  try { tag = browser ? Intl.getCanonicalLocales(browser)[0] ?? '' : ''; } catch { return 'en'; }
  return tag && normalizeLocale(tag) === (tag.split('-')[0] ?? '').toLowerCase() ? tag : 'en';
}

/** Payment timing only applies once the location has an account and pays per session. Absent enrollment is an older Api. */
const NOT_PER_SESSION: ReadonlySet<ElectricityEnrollment> = new Set(['manual', 'existing_agreement', 'transition_scheduled', 'not_launched']);
export type DepositSchedule = 'weekly_monday' | 'other' | null;
export function bankTimingKeys(status: { has_account?: boolean; deposit_schedule?: DepositSchedule; reimbursement?: { enrollment?: ElectricityEnrollment } } | null | undefined): SitePaymentCopyKey[] {
  if (!status?.has_account || (status.reimbursement?.enrollment && NOT_PER_SESSION.has(status.reimbursement.enrollment))) return [];
  return status.deposit_schedule === 'weekly_monday' ? ['settles', 'weeklyDeposits']
    : status.deposit_schedule === 'other' ? ['settles', 'stripeScheduleDeposits'] : ['settles'];
}

/** The local start of a picked calendar day (YYYY-MM-DD), so a signer east of UTC can record today. */
export function localDayStartIso(day: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const at = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return at.getFullYear() === Number(m[1]) && at.getMonth() === Number(m[2]) - 1 && at.getDate() === Number(m[3]) ? at.toISOString() : null;
}
